import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber } from "../lib/format";
import { useFilters, periodToDates } from "../lib/filters";
import { KPIStatCard } from "../components/KPIStatCard";
import { PageHeader } from "../components/PageHeader";
import { IconDocument, IconTruck, IconDollar } from "../components/icons";
import type { TransportVehicleCostDto } from "@bi/types";

/** "salario_comissao" → "Salário Comissão" (rótulos legíveis de categoria/sub-balde). */
const LABELS: Record<string, string> = {
  salario_comissao: "Salário / Comissão",
  frota_geral: "Frota (geral)",
  outros_sem_placa: "Outros (sem placa)",
  ipva: "IPVA",
};
const label = (s: string) => LABELS[s] ?? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function VehicleCostPage() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading } = useQuery({
    queryKey: ["transport", "vehicle-costs", from, to],
    queryFn: () =>
      api.get<TransportVehicleCostDto>(
        `/dashboards/transport/vehicle-costs?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ).catch(() => null),
    staleTime: 60_000,
  });

  const byCategory = data?.byCategory ?? [];
  const byPlate = data?.byPlate ?? [];
  const breakdown = data?.unallocated.semPlacaBreakdown ?? [];
  const allocated = byPlate.reduce((s, p) => s + p.total, 0);
  const naoAlocado = data ? data.unallocated.semPlaca + data.unallocated.estoque + data.unallocated.unresolved : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Custo por Placa"
        subtitle="Demonstrativo por conta financeira e centro de custo — custo por veículo e tipo de despesa"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPIStatCard
          label="Custo Total" memory={{ cardKey: "custo-placa.total", from, to, expected: data?.total }} value={data ? data.totalFormatted : "—"}
          status={data && data.total > 0 ? "warning" : "neutral"} loading={isLoading}
        />
        <KPIStatCard
          label="Custo Alocado a Placas" memory={{ cardKey: "custo-placa.alocado", from, to, expected: data ? allocated : undefined }} value={data ? fmtBRL(allocated) : "—"}
          valueSub={data ? `${fmtNumber(byPlate.length)} placas` : undefined}
          status="neutral" loading={isLoading}
        />
        <KPIStatCard
          label="Sem Placa" memory={{ cardKey: "custo-placa.sem-placa", from, to, expected: data?.unallocated.semPlaca }} value={data ? fmtBRL(data.unallocated.semPlaca) : "—"}
          valueSub="administrativo / terceiros / geral"
          status={data && data.unallocated.semPlaca > 0 ? "warning" : "neutral"} loading={isLoading}
        />
        <KPIStatCard
          label="Não Alocado (total)" memory={{ cardKey: "custo-placa.nao-alocado", from, to, expected: data ? naoAlocado : undefined }} value={data ? fmtBRL(naoAlocado) : "—"}
          valueSub="sem placa + estoque"
          status="neutral" loading={isLoading}
        />
      </div>

      {/* Categoria + Sub-baldes do sem-placa */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RankCard title="Custo por Tipo de Despesa" icon={IconDollar} loading={isLoading}
          rows={byCategory.map((c) => ({ key: label(c.category), sub: null, value: c.formatted }))}
          emptyMsg="Sem custo no período." />
        <RankCard title="Custos sem Placa — por grupo" icon={IconDocument} loading={isLoading}
          rows={[
            ...breakdown.map((b) => ({ key: label(b.bucket), sub: null, value: b.formatted })),
            ...(data && data.unallocated.estoque > 0 ? [{ key: "Estoque / Imobilizado", sub: null, value: fmtBRL(data.unallocated.estoque) }] : []),
            ...(data && data.unallocated.unresolved > 0 ? [{ key: "Centro não identificado", sub: null, value: fmtBRL(data.unallocated.unresolved) }] : []),
          ]}
          emptyMsg="Nenhum custo sem placa." />
      </div>

      {/* Ranking por placa */}
      <RankCard title="Custo por Veículo" icon={IconTruck} loading={isLoading}
        rows={byPlate.slice(0, 20).map((p) => ({
          key: p.plate,
          href: `/frota/placa/${encodeURIComponent(p.plate)}`,
          sub: p.byCategory.slice(0, 3).map((c) => label(c.category)).join(" · "),
          value: p.formatted,
        }))}
        emptyMsg="Sem custo por placa no período (rateio não sincronizado?)." />
    </div>
  );
}

function RankCard({
  title, icon: Icon, rows, loading, emptyMsg,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  rows: Array<{ key: string; sub: string | null; value: string; href?: string }>;
  loading: boolean;
  emptyMsg: string;
}) {
  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-surface-border flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-400" />
        <p className="text-sm font-semibold text-gray-900">{title}</p>
      </div>
      <div className="divide-y divide-surface-border">
        {loading ? (
          <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
        ) : rows.length === 0 ? (
          <p className="px-5 py-6 text-sm text-gray-400 text-center">{emptyMsg}</p>
        ) : rows.map((r, i) => (
          <div key={`${r.key}-${i}`} className="px-5 py-3 flex items-center gap-3">
            <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
            <div className="flex-1 min-w-0">
              {r.href ? (
                <Link to={r.href} className="text-sm font-medium text-tm-700 hover:underline truncate block">{r.key}</Link>
              ) : (
                <p className="text-sm font-medium text-gray-800 truncate">{r.key}</p>
              )}
              {r.sub && <p className="text-xs text-gray-400 truncate">{r.sub}</p>}
            </div>
            <p className="text-sm font-semibold text-gray-900 flex-shrink-0">{r.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
